import type { TranslationDictionary } from "./types";

export const pt: TranslationDictionary = {
  // ── EmailGate ──
  "page.title": "Sua Proposta | Woba",
  "emailGate.placeholder": "seu@email.com",
  "emailGate.invalidEmail": "Por favor, insira um e-mail válido",
  "emailGate.blockedEmail":
    "Por favor, insira um e-mail corporativo ou pessoal válido",
  "emailGate.loading": "Carregando...",
  "emailGate.notFound": "Proposta não encontrada",
  "emailGate.loadError":
    "Não foi possível carregar a proposta. Verifique o link ou tente novamente.",
  "emailGate.retry": "Tentar Novamente",
  "emailGate.proposalLoadError": "Erro ao carregar proposta. Tente novamente.",

  // ── ProposalCoverScreen (tela 1) ──
  "cover.ctaLabel": "Ver minha proposta",
  "cover.talkToConsultant": "Falar com a {{name}}",
  "cover.scheduleNote.bold": "Proposta válida até {{date}}.",
  "cover.scheduleNote.rest":
    "Garanta seu escritório: agende uma visita presencial ou por vídeo.",
  "cover.toggle.traditional": "Locação tradicional",
  "cover.toggle.woba": "Woba",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Proposta não encontrada ou expirada.",

  // ── Stepper / navegação por etapas ──
  "steps.comparison": "Vantagem Woba",
  "steps.spaces.singular": "Espaço Selecionado",
  "steps.spaces.plural": "Espaços Selecionados",
  "steps.scheduling": "Agendar Visita",
  "steps.proposal": "Fazer Proposta",
  "steps.briefing": "Outras opções",
  "stepper.next": "Próximo: {{name}}",
  "nav.back": "Voltar",
  "nav.continue": "Continuar",
  "nav.requestVisit": "Solicitar visita",
  "nav.backToRoomsList": "Voltar para a lista de escritórios",

  // ── Hero copy (used by ProposalCoverScreen) ──
  "hero.subtitle":
    "Abandone as complexidades do modelo tradicional e centralize tudo em um único contrato inteligente.",
  "hero.emailPrompt":
    "Sua proposta está pronta, insira seu e-mail para acessar.",

  // ── Chat ──
  "chat.contextLabel": "Tirando dúvidas sobre",
  "chat.placeholder": "Pergunte sobre este espaço...",
  "chat.welcomeLine1":
    "Olá! 👋 Estou aqui para tirar suas dúvidas sobre o **{{spaceName}}**.",
  "chat.welcomePrice":
    "O valor desse espaço é de **R$ {{price}}/posição/mês**.",
  "chat.welcomeAskAnything":
    "Pergunte o que quiser sobre esse espaço — se eu não souber responder na hora, vamos consultar o parceiro e te retornamos.",
  "chat.errorMessage":
    "Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.",
  "chat.dividerNowAbout": "Agora sobre: {{spaceName}}",
  "chat.sendAriaLabel": "Enviar",
  "chat.chip.monthlyValue": "Qual o valor mensal?",
  "chat.chip.parking": "Tem estacionamento?",
  "chat.chip.internet": "Qual a velocidade da internet?",
  "chat.chip.meetingRooms": "Salas de reunião inclusas?",
  "chat.chip.credits": "Como funcionam os créditos Woba?",
  "chat.chip.contract": "Qual a flexibilidade do contrato?",
  "chat.chip.scheduleVisit": "Quero agendar uma visita",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Espaços Selecionados",
  "spaces.optionsFor": "Opções para {{companyName}}",
  "spaces.optionsForDefault": "Opções para sua empresa",
  "spaces.bestOfficesInCity":
    "Selecionamos os melhores escritórios em {{city}} para a sua operação",
  "spaces.bestOptions": "Selecionamos as melhores opções para a sua operação",
  "spaces.availableOptions": "Opções Disponíveis",
  "spaces.recommended": "★ Recomendado",
  "spaces.combo": "Conjunto",
  "spaces.pos": "pos",
  "spaces.scopeOffice": "Escritório Privativo",
  "spaces.scopeSpace": "Área comum",
  "spaces.detail.roomId": "ID da sala",
  "spaces.detail.spaceId": "ID do espaço",
  "spaces.detail.back": "Voltar",
  "spaces.detail.close": "Fechar",
  "spaces.detail.expandPhotos": "Ver todas as fotos",
  "spaces.detail.officePhotosHeading": "Fotos do {{label}}",
  "spaces.detail.spacePhotosHeading": "Fotos da Área comum",
  "spaces.detail.positionsHeader": "Posições",
  "spaces.detail.sizeLabel": "Tamanho",
  "spaces.detail.pricePerPositionLabel": "Val. mensal / posição",
  "spaces.detail.summary":
    "Escritório privativo para operar com {{capacity}} posições e {{size}} m², no bairro {{neighborhood}} em {{city}}.",
  "spaces.detail.summaryNoSize":
    "Escritório privativo para operar com {{capacity}} posições, no bairro {{neighborhood}} em {{city}}.",
  "spaces.detail.summaryNoNeighborhood":
    "Escritório privativo para operar com {{capacity}} posições e {{size}} m², em {{city}}.",
  "spaces.detail.summaryNoSizeNoNeighborhood":
    "Escritório privativo para operar com {{capacity}} posições, em {{city}}.",
  "spaces.detail.summaryComboSuffix":
    " Este escritório faz parte de um conjunto de {{total}} escritórios reunidos para atender à sua solicitação completa.",
  "spaces.detail.spaceSummary":
    "A área comum do espaço conta com {{amenities}} comodidades disponíveis.",
  "spaces.detail.aboutOfficeTitle": "Sobre o escritório",
  "spaces.detail.aboutSpaceTitle": "Sobre a área comum",
  // Conformidade (PED-12757): documentos do prédio, nunca da sala — por isso
  // o tooltip nomeia o espaço como sujeito. Só estados positivos aparecem.
  "spaces.detail.complianceTitle": "Conformidade",
  "spaces.detail.complianceAvcb": "AVCB",
  "spaces.detail.complianceAvcbInfo":
    "Vistoria do Corpo de Bombeiros que atesta a segurança do prédio contra incêndio.",
  "spaces.detail.compliancePermit": "Alvará de funcionamento",
  "spaces.detail.compliancePermitInfo":
    "Licença da prefeitura que autoriza o funcionamento do prédio neste endereço.",
  "spaces.detail.amenitiesTitle": "Amenidades",
  "spaces.detail.amenitiesSubtitleOffice":
    "O que está disponível dentro do escritório privativo",
  "spaces.detail.amenitiesSubtitleSpace": "O que está disponível na área comum",
  "spaces.detail.locationTitle": "Localização",
  "spaces.tabNavLabel": "Navegação do espaço",
  "spaces.fullscreenTitle": "Localização dos Espaços",
  "spaces.mapButton": "Mostrar mapa",
  // Mapa mobile: o pino nomeia a sala igual ao card, e o chip de rodapé
  // substitui o antigo cabeçalho com seta como caminho de volta.
  "spaces.map.pinOffices": "{{count}} escritórios",
  "spaces.map.showList": "Mostrar lista",
  "spaces.viewMode.cards": "Ver em cards",
  "spaces.viewMode.list": "Ver em lista",
  "spaces.table.office": "Escritório",
  "spaces.table.monthlyValue": "Valor/mês",
  "spaces.table.pricePerPosition": "Custo/posição",
  "spaces.table.credits": "Diárias/mês",
  "spaces.table.creditBonusShort": "Ganhe {{credits}}",
  "spaces.mapLayers.button": "Pontos de interesse",
  "spaces.mapLayers.viewLabel": "Visualização",
  "spaces.mapLayers.view3d": "3D",
  "spaces.mapLayers.view2d": "2D",
  "spaces.mapLayers.poiLabel": "Pontos de interesse",
  "spaces.card.sqm": "{{value}} m²",
  "spaces.card.monthlyValue": "Valor mensal",
  "spaces.card.creditBonus": "Ganhe {{credits}} diárias/mês",
  "spaces.card.wantToVisit": "Selecionar",
  "spaces.card.selected": "Selecionado",
  "spaces.card.visitRequested": "Visita solicitada",
  "spaces.card.proposalSent": "Proposta enviada",
  "spaces.card.spaceHasAvcb": "Espaço com AVCB",
  "spaces.card.spaceHasPermit": "Espaço com alvará",
  "spaces.card.complianceTooltip":
    "O espaço onde esta sala está possui o documento",
  "spaces.card.viewDetails": "Ver detalhes",
  "spaces.card.spacePhoto": "Área comum",
  "spaces.card.comboRoomLabel": "Escritório {{number}}",
  "spaces.card.comboOfficesCount": "{{count}} escritórios",
  "spaces.card.detailsTitle": "Detalhes do espaço",
  "spaces.header.selectToVisit": "Escolha o espaço ideal",
  "spaces.header.title.singular": "1 Escritório Privativo",
  "spaces.header.title.plural": "{{count}} Escritórios Privativos",
  "spaces.header.title.filteredSingular":
    "{{count}} de {{total}} Escritório Privativo",
  "spaces.header.title.filteredPlural":
    "{{count}} de {{total}} Escritórios Privativos",
  "spaces.header.lead":
    "Cada opção passou pela curadoria Woba. Nenhuma por acaso.",
  "spaces.header.briefing.singular":
    "Escolhemos um Escritório Privativo para o seu pedido: {{details}}.",
  "spaces.header.briefing.plural":
    "Escolhemos alguns Escritórios Privativos para o seu pedido: {{details}}.",
  "spaces.header.briefing.inCity": "em {{city}}",
  "spaces.header.briefing.positionsExact": "{{count}} posições",
  "spaces.header.briefing.contract": "contrato de {{months}} meses",
  "spaces.filters.sortBy": "Ordenar por",
  "spaces.filters.sortRecommended": "Recomendado",
  "spaces.filters.sortCheapest": "Mais barato",
  "spaces.filters.sortMostPositions": "Mais posições",
  "spaces.filters.capacity": "Capacidade",
  "spaces.filters.priceRange": "Faixa de preço",
  "spaces.filters.size": "Tamanho",
  "spaces.filters.capacityUpTo": "Até {{max}} posições",
  "spaces.filters.capacityBetween": "{{min}} a {{max}} posições",
  "spaces.filters.capacityAbove": "Mais de {{min}} posições",
  "spaces.filters.priceUpTo": "Até R$ {{max}}",
  "spaces.filters.priceBetween": "R$ {{min}} a R$ {{max}}",
  "spaces.filters.priceAbove": "Acima de R$ {{min}}",
  "spaces.filters.sizeUpTo": "Até {{max}} m²",
  "spaces.filters.sizeBetween": "{{min}} a {{max}} m²",
  "spaces.filters.sizeAbove": "Mais de {{min}} m²",
  "spaces.filters.noResults": "Nenhum espaço encontrado com esses filtros.",
  "spaces.selectedBar.hintEmpty": "Selecione um espaço para continuar",
  "spaces.selectedBar.hintSelected": "Escolha como deseja prosseguir",

  // ── PhotosTab ──
  "photos.prevPhoto": "Foto anterior",
  "photos.nextPhoto": "Próxima foto",
  "photos.noImage": "Sem imagem disponível",
  "photos.didYouLike": "Gostou?",
  "photos.liked": "Gostei",
  "photos.disliked": "Não gostei",
  "photos.positions": "posições",
  "photos.perPosition": "R$ {{price}} / posição",
  "photos.priceOnRequest": "Valor sob consulta",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee incluso",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Informações de amenidades não disponíveis.",
  "amenities.showOthers": "Outras ({{count}})",
  "amenities.showLess": "Ver menos",

  // ── RoomEmbeddedMap (distance calculator) ──
  "map.calculateDistance": "Calcular distância entre o espaço e...",
  "map.placeholder": "Ex: Av. Paulista, 1000",
  "map.searching": "Buscando...",
  "map.calculate": "Calcular",
  "map.distancesFrom": "Distâncias a partir de",
  "map.addressNotProvided": "Endereço não informado",
  "map.selectedNoAddress": "Endereço não informado para esta opção",
  "map.recenter": "Recentralizar",
  "map.recenterTitle": "Recentralizar mapa",
  "map.clickToInteract": "Clique para interagir com o mapa",
  "map.locationNotFound":
    "Não encontramos esse endereço. Tente buscar de outra forma.",
  "advantages.goToSlide": "Ir para o destaque {{n}}",
  "visitDialog.title": "Agendar visitas",
  "visitDialog.description":
    "Neste momento, seriam enviadas comunicações para os Parceiros selecionados, verificando a disponibilidade da visita às suas salas.",
  "poi.noDescription": "Sem descrição",

  // ── ComparisonSection ──
  "comparison.heading":
    "A forma *mais inteligente* de contratar e administrar escritórios",

  // ── ComparisonSection (conteúdo "Vantagem Woba") ──
  "advantage.heading": "Ter um escritório *pode ser fácil*.",
  "advantage.subheadingPre": "Com a Woba, você tem acesso a",
  "advantage.subheadingBold": "muito mais do que um escritório",
  "advantage.subheadingPost":
    ": tudo simples, prático e sem burocracia. E sem pagar nada a mais por isso.",
  "advantage.cardsAriaLabel": "Vantagens Woba",
  "advantage.card1.title": "Melhor preço garantido",
  "advantage.card1.descPre": "Com a Woba,",
  "advantage.card1.descBold": "pague o mesmo valor mensal",
  "advantage.card1.descPost":
    "que pagaria direto com o parceiro, com até 15% de desconto negociado — e ainda leve toda a rede.",
  "advantage.card1.sealAlt": "Selo de melhor preço garantido",
  "advantage.card1.tagline":
    "Achou preço melhor, dentro ou fora da rede? A gente cobre.",
  "advantage.card2.title": "Muito além de um escritório",
  "advantage.card2.stat": "3.000+ espaços · 400+ cidades",
  "advantage.card2.descPre": "Seu time com acesso a",
  "advantage.card2.descBold": "salas de reunião e diárias em coworking",
  "advantage.card2.descPost":
    "em mais de 3.000 espaços da Rede Woba em todo o Brasil, sem pagar nada a mais por isso.",
  "advantage.card2.badge": "Ganhe horas para uso na rede",
  "advantage.card3.title": "Seu time de facilities com super poderes",
  "advantage.card3.descPre": "Uma equipe de",
  "advantage.card3.descBold": "agentes de IA",
  "advantage.card3.descPost":
    "cuida da sua operação — acessos, reservas, manutenção e contratos — tudo resolvido num boleto só, sem burocracia.",
  "advantage.card4.title": "Escritório como serviço",
  "advantage.card4.descBold": "A flexibilidade que você precisa:",
  "advantage.card4.descPost":
    "aumente, reduza ou mude de cidade quando o negócio pedir. Sem multa, sem CAPEX, sem obra, sem contrato longo.",

  // ── CostBreakdownDrawer ("Entenda o preço" badge no card) ──
  "costBreakdown.badge": "Entenda o preço",
  "costBreakdown.badgeWithScope": "Entenda o preço - {{scope}}",
  "costBreakdown.drawerTitle": "Tudo isso, num boleto só.",
  "costBreakdown.drawerSubtitle": "Seu time cuida do negócio, não do prédio.",
  "costBreakdown.compositionTitle": "Como funciona a sua locação na Woba",
  "costBreakdown.compositionBodyPre":
    "Essa mensalidade é formada por 3 grupos de custo, os mesmos de uma locação tradicional. Aqui, eles se juntam em um valor único, com uma única data de vencimento, sem planilha para conciliar.",
  "costBreakdown.compositionBodyBold": "Tudo isso, em um boleto só.",
  "costBreakdown.totalLabel": "Total mensal",
  "costBreakdown.detailTitle": "Como funciona uma locação tradicional",
  "costBreakdown.footnotePre":
    "Numa locação tradicional, seu boleto mensal se divide em 3 grandes grupos. Esses grupos têm diversos itens dentro, o que resulta em {{count}} itens para gerenciar: {{count}} fornecedores, {{count}} contratos, cada um com sua própria data de vencimento e seu próprio ponto de contato. No fim, é o seu time que carrega esse peso todo mês.",
  "costBreakdown.category.rent": "Aluguel + Condomínio + IPTU",
  "costBreakdown.category.services": "Serviços Operacionais",
  "costBreakdown.category.support": "Suporte e Depreciação",
  "costBreakdown.item.rentBase": "Aluguel base",
  "costBreakdown.item.condo": "Condomínio",
  "costBreakdown.item.iptu": "IPTU",
  "costBreakdown.item.energy": "Energia",
  "costBreakdown.item.staff": "Staff operacional",
  "costBreakdown.item.cleaning": "Limpeza",
  "costBreakdown.item.internet": "Internet / Telecom",
  "costBreakdown.item.maintenance": "Manutenção",
  "costBreakdown.item.water": "Água",
  "costBreakdown.item.furniture": "Mobiliário (depreciação)",
  "costBreakdown.item.amenities": "Café / Amenities",
  "costBreakdown.item.insurance": "Seguros",
  "costBreakdown.item.admin": "Admin / Overhead",

  // ── CTASection / Footer ──
  "cta.tagline":
    "A forma mais inteligente de contratar e administrar escritórios",
  "cta.contact": "Contato",
  "cta.proposal": "Proposta",
  "cta.validUntil": "Válida até:",
  "cta.validTBD": "A definir",
  "cta.copyright": "© {{year}} Woba. Todos os direitos reservados.",

  // ── Logos marquee (ProposalCoverScreen footer) ──
  "logos.socialProof": "Faça como milhares de empresas que confiam na Woba",

  // ── VisitSchedulingStep ──
  "schedule.overline": "Agende sua visita",
  "schedule.title": "Você *escolhe* como quer conhecer.",
  "schedule.subtitle":
    "Quanto mais a fundo você for, mais entende como a Woba funciona na prática — e quanto ela economiza para a {{companyName}}.",
  "schedule.subtitleNoCompany":
    "Quanto mais a fundo você for, mais entende como a Woba funciona na prática — e quanto ela pode economizar para você.",
  "schedule.instruction":
    "Escolha o dia, o horário e o formato da visita para cada espaço.",
  "schedule.dateTimeLabel": "Dia e horário",
  "schedule.previousDates": "Dias anteriores",
  "schedule.nextDates": "Próximos dias",
  "schedule.modalityLabel": "Formato da visita",
  "schedule.modalityInPerson": "Presencial",
  "schedule.modalityOnline": "Videochamada",
  "schedule.modalityTestDrive": "Test-drive",
  "schedule.modalityExplainerInPerson":
    "Real e de perto. Conheça o espaço pessoalmente, veja a estrutura de perto e sinta como seria a rotina do seu time ali.",
  "schedule.modalityExplainerOnline":
    "Prático e imediato. Agende uma visita por videochamada para conhecer o escritório e tirar dúvidas direto com o espaço.",
  "schedule.modalityExplainerTestDrive":
    "Completo, sem pressa. Use o escritório por um dia inteiro com sua equipe antes de decidir, sem custo.",
  "schedule.testDriveLearnMore": "Saiba mais",
  "schedule.testDriveDrawerTitle": "Test-drive de 1 dia",
  "schedule.testDriveDrawerSubtitle":
    "Completo, sem pressa. Use o escritório por um dia inteiro com sua equipe antes de decidir, sem custo.*",
  "schedule.testDriveDrawerPhotoAlt":
    "Time trabalhando em um espaço compartilhado da Woba",
  "schedule.testDriveHowItWorksTitle": "Como funciona",
  "schedule.testDriveStep1":
    "Escolha a data disponível no calendário — sem horário marcado, é o dia inteiro.",
  "schedule.testDriveStep2":
    "A Woba confirma a disponibilidade direto com o espaço.",
  "schedule.testDriveStep3":
    "Você recebe a confirmação e leva seu time no dia combinado.",
  "schedule.testDriveStep4":
    "Decide com base na experiência real, não só em fotos.",
  "schedule.testDriveBenefitsTitle": "O que você ganha",
  "schedule.testDriveBenefit1":
    "Testa a rotina do seu time no espaço antes de fechar.",
  "schedule.testDriveBenefit2": "Sem custo — a visita é por conta da Woba.",
  "schedule.testDriveBenefit3":
    "Mais segurança para decidir, com dados reais do dia a dia.",
  "schedule.testDriveClosingNote":
    "* Sujeito à confirmação do espaço, dentro do horário de funcionamento dele.",
  "schedule.testDriveNoteLabel": "Por que você quer testar esse espaço?",
  "schedule.testDriveNotePlaceholder":
    "Conte o que chamou sua atenção nesse escritório e o que quer confirmar com o test-drive",
  "schedule.testDriveSingleRoomError":
    "Test-drive é só para 1 escritório por vez. Remova os outros para continuar.",
  "schedule.visitorCountLabel": "Quantas pessoas vão à visita?",
  "schedule.visitorCountPlaceholder": "Ex: 4 pessoas",
  "schedule.valueBullet1":
    "Melhor preço garantido — o mesmo que negociando direto.",
  "schedule.valueBullet2": "Ganhe créditos para salas de reunião e coworking.",
  "schedule.valueBullet3":
    "Flexibilidade para aumentar, reduzir ou mudar de cidade.",
  "schedule.visitTypeInPerson": "Visita presencial",
  "schedule.visitTypeOnline": "Visita por videochamada",
  "schedule.visitTypeTestDrive": "Test-drive (1 dia)",
  "schedule.slotTakenHint": "Esse horário já foi escolhido para outro espaço",
  "schedule.fewSlotsAlert":
    "Poucos horários disponíveis. Escolha o seu e garanta a sua visita.",
  "schedule.confirm": "Solicitar visita",
  "schedule.subStepAriaLabel": "Etapas do agendamento",
  "schedule.subStepWhen": "Dia e horário",
  "schedule.subStepData": "Seus dados",
  "schedule.dataOverline": "Quase lá",
  "schedule.dataTitle": "Quem vai *participar*?",
  "schedule.dataSubtitle":
    "Complete os dados de quem vai participar da visita.",
  "schedule.hintIncomplete":
    "Selecione dia, horário e formato para cada espaço",
  "schedule.hintContactIncomplete": "Preencha os campos para continuar",
  "schedule.fieldEmail": "E-mail",
  "schedule.fieldEmailPlaceholder": "seu@email.com",
  "schedule.fieldEmailError": "Preencha um e-mail válido",
  "schedule.fieldWhatsappError": "Preencha adequadamente o número de telefone",
  "schedule.addParticipant": "Incluir outro participante",
  "schedule.removeVisit": "Remover este espaço da visita",
  "schedule.removeParticipant": "Remover participante",
  "schedule.participantLabel": "Participante {{n}}",
  "schedule.fieldName": "Nome",
  "schedule.fieldNamePlaceholder": "Seu nome",
  "schedule.fieldJobTitle": "Cargo",
  "schedule.fieldJobTitlePlaceholder": "Seu cargo",
  "schedule.fieldWhatsapp": "WhatsApp (recomendado)",
  "schedule.fieldWhatsappRequired": "WhatsApp",
  "schedule.fieldWhatsappPlaceholder": "(11) 90000-0000",
  "schedule.fieldCpf": "CPF",
  "schedule.fieldCpfPlaceholder": "000.000.000-00",
  "schedule.fieldCpfError": "Preencha um CPF válido",
  "schedule.fieldCpfHint":
    "Usado só para liberar seu acesso ao espaço no dia do test-drive.",
  "privacy.consentPrefix":
    "Ao enviar este formulário, você declara estar ciente da nossa",
  "privacy.consentLinkLabel": "Política de Privacidade",
  "privacy.consentSuffix":
    ". Seus dados serão utilizados para viabilizar o atendimento solicitado.",
  "schedule.successTitle": "Solicitação enviada!",
  "schedule.successSubtitle":
    "Sua solicitação foi enviada. Fique de olho no seu e-mail: avisamos assim que o parceiro confirmar o horário.",
  "schedule.successNotice":
    "As condições da sua proposta estão garantidas até {{date}}.",
  "schedule.fullAddressNotice": "Endereço completo enviado na confirmação.",
  "schedule.postStep0Label": "Solicitação enviada",
  "schedule.postStep0Time": "Concluído",
  "schedule.toBeConfirmed": "a confirmar",
  "schedule.postStep1Label": "Visitar escritório",
  "schedule.postStep2Label": "Proposta final",
  "schedule.postStep3Label": "Assine e ocupe",
  "schedule.postStep1Time": "A partir de 48h",
  "schedule.postStep2Time": "48h após visita",
  "schedule.postStep3Time": "Até 15 dias",
  "schedule.confirmationOverline": "Próximos passos",
  "schedule.confirmationTitle": "Do interesse à operação, *em poucos dias*.",
  "schedule.confirmationSubtitle":
    "Acompanhe o que vem a seguir até você ocupar o novo escritório.",
  "schedule.recapOverline": "Rede Woba",
  "schedule.recapHeading":
    "Você contrata um escritório.\nLeva um país inteiro.",
  "schedule.recapSubheading":
    "5% do valor do seu contrato vira bônus em créditos, pra sua equipe não ficar presa a um único endereço. Ela pode usar salas de reunião, estações de trabalho e espaços de evento em qualquer cidade do Brasil.",
  "schedule.recapPricePre": "E o preço?",
  "schedule.recapPriceBold":
    "Sempre o mesmo que você pagaria negociando direto",
  "schedule.recapPricePost": "— sem custo extra.",
  "schedule.recapStat1Value": "+3.000",
  "schedule.recapStat1Label": "escritórios flexíveis",
  "schedule.recapStat2Value": "+4.200",
  "schedule.recapStat2Label": "salas de reunião",
  "schedule.recapStat3Value": "+1.000",
  "schedule.recapStat3Label": "espaços de eventos",
  "schedule.recapStat4Value": "+400",
  "schedule.recapStat4Label": "cidades na LatAm",
  "schedule.recapType1Title": "Estações de Trabalho",
  "schedule.recapType1DescPre": "1 diária em qualquer uma das",
  "schedule.recapType1DescCount": "+23.000",
  "schedule.recapType1DescPost": "estações",
  "schedule.recapType2Title": "Salas de Reunião",
  "schedule.recapType2DescPre": "1 hora em qualquer uma das",
  "schedule.recapType2DescCount": "+4.200",
  "schedule.recapType2DescPost": "salas",
  "schedule.recapType3Title": "Espaços de Eventos",
  "schedule.recapType3DescPre": "1 diária em qualquer um dos",
  "schedule.recapType3DescCount": "+1.000",
  "schedule.recapType3DescPost": "espaços",
  "schedule.recapType4Title": "Escritórios Privativos",
  "schedule.recapType4DescPre": "1 mês em qualquer um dos",
  "schedule.recapType4DescCount": "+5.200",
  "schedule.recapType4DescPost": "escritórios",
  "schedule.recapCredits": "créditos",
  "schedule.recapCreditsPerPerson": "créditos/pessoa",
  "schedule.recapCreditsPerPosition": "créditos/posição",

  // ── Spaces (additional) ──
  "spaces.option": "Escritório Privativo {{number}}",
  "spaces.ofTotal": "de {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurantes",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Farmácias",
  "poi.transport": "Transporte",
  "poi.hotel": "Hotéis",
  "poi.parking": "Estacionamentos",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "este espaço",

  // ── CreditInfoDrawer ──
  "creditDrawer.viewDetails": "Ver detalhes dos créditos",
  "creditDrawer.title": "Créditos inclusos na sua contratação",
  "creditDrawer.subtitle":
    "5% do valor do seu contrato vira bônus em créditos.",
  "creditDrawer.creditsLabel": "Créditos/mês inclusos*",
  "creditDrawer.creditsValue": "+{{credits}} créditos",
  "creditDrawer.creditsFootnote":
    "* Os créditos não são cumulativos: o que não for usado no mês não passa para o mês seguinte.",
  "creditDrawer.howItWorksBody":
    "Sua equipe não fica presa a este endereço: com os créditos inclusos, ela pode usar salas de reunião, estações de trabalho e espaços de evento em qualquer cidade do Brasil — sem gastar nada a mais por isso.",
  "creditDrawer.formatMeetingRoomTitle": "Salas de reunião",
  "creditDrawer.formatMeetingRoomBody":
    "{{credits}} créditos equivalem a {{hours}}h de sala de reunião para até 4 pessoas — o valor muda conforme o tamanho da sala.",
  "creditDrawer.formatCoworkingTitle": "Coworking (estações compartilhadas)",
  "creditDrawer.formatCoworkingBody":
    "{{credits}} créditos equivalem a {{credits}} diárias de uso em espaços compartilhados.",
  "creditDrawer.formatPrivateOfficeTitle": "Escritórios privativos por diária",
  "creditDrawer.formatPrivateOfficeBody":
    "Use créditos para montar estruturas temporárias para projetos ou times.",
  "creditDrawer.formatEventSpaceTitle": "Espaços para eventos e treinamentos",
  "creditDrawer.formatEventSpaceBody":
    "Reserve salas maiores e auditórios usando múltiplos créditos.",
  "creditDrawer.quadroHoursLabel": "por mês",
  "creditDrawer.quadroDaysLabel": "diárias/mês",
  "creditDrawer.onRequest": "Sob consulta",
  "creditDrawer.practiceTitle": "Na prática",
  "creditDrawer.practiceDayToDayTitle": "Como isso se aplica no seu dia a dia",
  "creditDrawer.practiceBullet1":
    "Seu escritório deixa de ser um único endereço",
  "creditDrawer.practiceBullet2":
    "Você escolhe o formato certo pra cada momento, sem precisar de contrato novo",
  "creditDrawer.practiceBullet3":
    "O crédito já está incluso — não é custo extra no seu boleto",
  "creditDrawer.practiceCta":
    "Em vez de contratar mais espaço, você usa créditos quando precisa.",

  // ── CreditInfoDrawer, variante "créditos extras" (ENT, PED-12653) ──
  "extraCreditDrawer.viewDetails": "Ver detalhes dos créditos extras",
  "extraCreditDrawer.title": "Créditos extras na sua contratação",
  "extraCreditDrawer.subtitle":
    "Um pacote opcional de créditos, além dos inclusos, para usar na rede Woba.",
  "extraCreditDrawer.creditsLabel": "Créditos extras/mês*",
  "extraCreditDrawer.monthlyValueLabel": "Valor do pacote/mês",
  "extraCreditDrawer.howItWorksBody":
    "Além dos créditos inclusos, este pacote dá à sua equipe mais créditos por mês para usar salas de reunião, estações de trabalho e espaços de evento em qualquer cidade do Brasil — e ele entra no seu total mensal.",
  "extraCreditDrawer.practiceBullet3":
    "O pacote é opcional e entra no seu total mensal — sem contrato à parte",

  // ── RoomPriceCard (card de preço no drawer de detalhes, desktop) ──
  "priceCard.monthlyLabel": "Mensalidade do escritório",
  "priceCard.monthlyLabelWithScope": "Mensalidade - {{scope}}",
  "priceCard.perPositionValue": "{{price}} / posição",

  // ── PriceGuaranteeModal ──
  "priceGuarantee.title": "Garantia de melhor preço",
  "priceGuarantee.subtext": "Mesmo preço do parceiro, com até 15% de desconto",
  "priceGuarantee.bodyPre":
    "Negociamos direto com o parceiro pra garantir o melhor preço da sua proposta. Conseguimos ",
  "priceGuarantee.bodyBold": "até 15% de desconto",
  "priceGuarantee.bodyPost": " sobre o valor do escritório.",
  "priceCard.creditsLabel": "Créditos inclusos",
  "priceCard.freeBadge": "grátis",
  "priceCard.creditsSubtext":
    "Equivalente a {{credits}} diárias em espaços compartilhados/mês",
  "priceCard.flexCreditsLabel": "Créditos extras (opcional)",
  "priceCard.setupFeeLabel": "Setup Fee",
  "priceCard.setupFeeCaption": "Pago uma única vez",
  "priceCard.licenseFeeLabel": "License Fee",
  "priceCard.licenseFeeSemiannualCaption": "Pago semestralmente",
  "priceCard.licenseFeeAnnualCaption": "Pago anualmente",
  "priceCard.helpLink": "Ainda tem dúvidas? Fale com a gente",
  "priceCard.scheduleVisit": "Solicitar visita",
  "priceCard.makeProposal": "Fazer proposta",

  // ── ProposalMakingStep (fluxo "Fazer proposta") ──
  // Mesmo padrão de eyebrow + título com ênfase + subtítulo do
  // VisitSchedulingStep — "Fazer proposta" lê como a mesma jornada, só um
  // caminho diferente.
  "proposalMaking.hub.overline": "Faça sua proposta",
  "proposalMaking.hub.headline": "Você *decide* como avançar.",
  "proposalMaking.hub.subtitle":
    "Revise os valores e o resumo da sala antes de escolher os próximos passos para a {{companyName}}.",
  "proposalMaking.hub.subtitleNoCompany":
    "Revise os valores e o resumo da sala antes de escolher os próximos passos.",
  "proposalMaking.subStepDefine": "Condições da proposta",
  "proposalMaking.subStepAdjust": "Ajuste de preço e do espaço",
  "proposalMaking.subStepReview": "Identificação",
  "proposalMaking.subStepAriaLabel": "Etapas da proposta",
  "proposalMaking.choose.pickerLabel": "Escolha uma opção",
  "proposalMaking.choose.acceptLabel": "Aceitar condições",
  "proposalMaking.choose.acceptDescription":
    "Aceitar as condições atuais agiliza a análise e coloca sua proposta na frente de quem está negociando.",
  "proposalMaking.choose.negotiateLabel": "Negociar valor ou pedir ajuste",
  "proposalMaking.choose.negotiateDescription":
    "Proponha um valor diferente ou peça ajustes no espaço — nosso time avalia e responde em até 2 dias úteis.",
  "proposalMaking.negotiate.overline": "Negociar valor",
  "proposalMaking.negotiate.headline": "Proponha o valor *ideal* pra você.",
  "proposalMaking.negotiate.subtitle":
    "Ajuste o valor mensal proposto e conte pro nosso time por que ele faz sentido pra você — quanto mais próximo do valor atual, maior a chance de aceite rápido pelo parceiro.",
  "proposalMaking.negotiate.subtitleNoPrice":
    "Conte pro nosso time o que você gostaria de propor e por que faz sentido pra você — ele avalia junto com o parceiro e responde em seguida.",
  "proposalMaking.negotiate.sectionTitle": "Ajuste de preço",
  "proposalMaking.negotiate.fieldLabel": "Sua proposta",
  "proposalMaking.negotiate.fieldPlaceholder": "0",
  "proposalMaking.negotiate.goodProposalTitle": "Ótima proposta!",
  "proposalMaking.negotiate.goodProposalDescription":
    "Valor igual ou maior que o atual tem alta chance de ser aceito.",
  "proposalMaking.negotiate.moderateDiscountTitle": "Boa chance de aceite",
  "proposalMaking.negotiate.moderateDiscountDescription":
    "Descontos de até 15% têm boas chances de ser aprovados pelo parceiro.",
  // Bloqueia o envio em vez de avisar: um corte maior não é algo que o
  // parceiro consiga avaliar, então a mensagem nomeia o limite e já dá o
  // valor que destrava o Continuar.
  "proposalMaking.negotiate.overLimitTitle": "Desconto acima do limite",
  "proposalMaking.negotiate.overLimitDescription":
    "Conseguimos enviar propostas com até {{percent}}% de desconto. Ajuste para {{minimum}} ou mais para continuar.",
  "proposalMaking.negotiate.overLimitHint":
    "Ajuste a proposta para {{minimum}} ou mais para continuar",
  "proposalMaking.negotiate.discountBadge": "-{{percent}}%",
  "proposalMaking.negotiate.increaseBadge": "+{{percent}}%",
  "proposalMaking.negotiate.reasonLabel": "Conte o motivo da sua proposta",
  "proposalMaking.negotiate.reasonPlaceholder":
    "Conte pra gente o motivo do ajuste de valor...",
  "proposalMaking.negotiate.flexCreditsLabel": "Créditos extras por mês",
  "proposalMaking.negotiate.flexCreditsUnit": "créditos",
  "proposalMaking.negotiate.flexCreditsHelper":
    "Equivale a {{value}}/mês. A negociação acima vale apenas para a mensalidade.",
  "proposalMaking.adjustmentsChoice.pickerLabel":
    "Ajuste de configurações do espaço",
  "proposalMaking.adjustmentsChoice.overline": "Ajustes no espaço",
  "proposalMaking.adjustmentsChoice.headline": "Precisa de algum *ajuste*?",
  "proposalMaking.adjustmentsChoice.subtitle":
    "Você pode pedir para retirar, instalar, reparar ou substituir item.",
  "proposalMaking.adjustmentsChoice.noneLabel": "Sem ajustes",
  "proposalMaking.adjustmentsChoice.noneBadge": "Maior chance de ser aceita",
  "proposalMaking.adjustmentsChoice.someLabel": "Com ajustes",
  "proposalMaking.adjustmentsChoice.someBadge": "Sujeito à avaliação do espaço",
  "proposalMaking.adjustmentType.overline": "Tipo de ajuste",
  "proposalMaking.adjustmentType.headline": "Qual é o tipo do *ajuste*?",
  "proposalMaking.adjustmentType.subtitle":
    "Você pode adicionar um ajuste por vez.",
  "proposalMaking.adjustmentType.layout": "Layout",
  "proposalMaking.adjustmentType.layoutDescription":
    "Retirar ou instalar um item, mudar ou substituir algo no espaço.",
  "proposalMaking.adjustmentType.maintenance": "Manutenção ou reparo",
  "proposalMaking.adjustmentType.maintenanceDescription":
    "Consertar ou fazer manutenção em algo que já existe.",
  "proposalMaking.adjustmentType.positions": "Posições",
  "proposalMaking.adjustmentType.positionsDescription":
    "Aumentar ou reduzir o número de posições do escritório.",
  "proposalMaking.adjustmentDescription.overline": "Detalhe o ajuste",
  "proposalMaking.adjustmentDescription.headline":
    "Descreva o que você *precisa*.",
  "proposalMaking.adjustmentDescription.subtitle":
    "Quanto mais detalhes, mais rápido avaliamos o pedido.",
  "proposalMaking.adjustmentDescription.placeholder":
    "Descreva o que você precisa...",
  "proposalMaking.adjustmentDescription.continueLabel": "Revisar",
  "proposalMaking.review.overline": "Quase lá",
  "proposalMaking.review.headline": "Revise sua *proposta*.",
  "proposalMaking.review.subtitle":
    "Confirme os valores e ajustes antes de enviar pro nosso time.",
  "proposalMaking.review.bannerAccepted": "Essa é uma ótima proposta!",
  "proposalMaking.review.bannerNegotiating":
    "Sua proposta será enviada para análise do nosso parceiro.",
  "proposalMaking.review.adjustmentsTitle": "Ajustes solicitados",
  "proposalMaking.review.addAdjustment": "Adicionar ajuste",
  "proposalMaking.review.removeAdjustment": "Remover",
  "proposalMaking.review.identificationTitle": "Seus dados",
  "proposalMaking.review.occupancyDateLabel": "Data prevista de ocupação",
  "proposalMaking.review.contractTermLabel": "Prazo de contrato",
  "proposalMaking.review.contractTermOption.12": "12 meses",
  "proposalMaking.review.contractTermOption.24": "24 meses",
  "proposalMaking.review.contractTermOption.36": "36 meses",
  "proposalMaking.review.contractTermOption.48": "48 meses",
  "proposalMaking.review.contractTermOption.other": "Outro",
  "proposalMaking.review.contractTermCustomPlaceholder": "Quantos meses?",
  "proposalMaking.review.submitLabel": "Confirmar e enviar",
  "proposalMaking.sent.overline": "Próximos passos",
  "proposalMaking.sent.headline": "Proposta enviada!",
  "proposalMaking.sent.subtitle": "Sua proposta foi enviada para o time Woba.",
  "proposalMaking.sent.notice": "Essa proposta é válida até {{date}}.",
  "proposalMaking.sent.roomStatusLabel": "Proposta enviada",
  "proposalMaking.sent.postStep0Label": "Proposta enviada",
  "proposalMaking.sent.postStep0Time": "Concluído",
  "proposalMaking.sent.postStep1Label": "Em análise",
  "proposalMaking.sent.postStep1Time": "Resposta em até 2 dias úteis",
  "proposalMaking.sent.postStep2Label": "Aprovação",
  "proposalMaking.sent.postStep2Time": "Após retorno do parceiro",
  "proposalMaking.sent.postStep3Label": "Assine e ocupe",
  "proposalMaking.sent.postStep3Time": "Até 15 dias",

  // ── Briefing aprofundado via proposta (PED-14146) ──
  // Dois cards e dois fluxos, escolhidos pelo porte do deal: abaixo de 30
  // posições o cliente conta o que faltou; de 30 para cima ele escolhe
  // entre mais prontos, ajustes ou obra sob medida.
  "briefing.card.title": "Podemos ir\nalém destas\nopções",
  "briefing.card.subtitle":
    "Nos conte o que você precisa. A gente faz o resto:",
  "briefing.card.cta": "Contar o que preciso",
  "briefing.card.smallTeam.bullet1": "Você diz o que não serviu",
  "briefing.card.smallTeam.bullet2": "Trazemos novas opções",
  "briefing.card.smallTeam.bullet3": "Ou ajustamos a sala escolhida",
  "briefing.card.smallTeam.bullet4": "Sem recomeçar do zero",
  "briefing.card.largeTeam.bullet1": "Mais opções na nossa rede",
  "briefing.card.largeTeam.bullet2": "Adaptado antes de você entrar",
  "briefing.card.largeTeam.bullet3": "Ou desenhado do zero",
  "briefing.card.largeTeam.bullet4": "A obra é com a gente",

  // Etapas — 3 em cada fluxo (uma barra maior que isso vira tarefa).
  "briefing.stepper.ariaLabel": "Etapas do formulário",
  "briefing.stepper.adjust": "Ajustes",
  "briefing.stepper.intent": "O que procura",
  "briefing.stepper.space": "O espaço",
  "briefing.stepper.contact": "Seus dados",
  "briefing.stage.overline": "Etapa {{number}} de {{total}}",
  "briefing.submitLabel": "Enviar",

  // Fluxo A · etapa 1

  // Fluxo A · caminho 1 — ajustar a sala
  "briefing.adjust.title": "Vamos *ajustar* a sala que agradou",
  "briefing.adjust.subtitle":
    "Só faltaram detalhes da sala. Diga qual delas você gostou e o que mudaria.",
  "briefing.adjust.spaceLabel": "A sala que mais agradou",
  "briefing.adjust.spacePlaceholder": "Escolha uma das opções da proposta",
  "briefing.adjust.label":
    "Gostou dessa sala, mas faria algum ajuste para ela ficar ideal para o seu time? Conte pra gente.",
  "briefing.adjust.groupStructural": "Mudanças na estrutura",
  "briefing.adjust.structuralHelp":
    "Mudanças na estrutura viram um projeto personalizado: o prazo e o investimento mudam, e um time específico assume o seu pedido.",
  "briefing.adjust.simpleNotesLabel": "Quer detalhar esses ajustes?",
  "briefing.adjust.simpleNotesPlaceholder":
    "Ex.: trocar as mesas por estações de 4 lugares e virar o layout para a janela.",
  "briefing.adjust.structuralNotesLabel": "Quer detalhar essas mudanças?",
  "briefing.adjust.structuralNotesPlaceholder":
    "Ex.: fechar a sala do fundo com divisória de vidro para virar sala de reunião.",
  "briefing.adjust.notesLabel": "Algo mais que você mudaria?",
  "briefing.adjust.notesPlaceholder":
    "Ex.: trocaria as mesas por estações de 4 lugares.",

  // Fluxo A · caminho 2 — nova busca

  // Fluxo B · pergunta 00
  // "Outros escritórios prontos" — avaliação sala a sala, mesma taxonomia
  // do polegar da listagem, alimentando o mesmo `rateSpace`.
  "briefing.stepper.feedback": "As opções",
  "briefing.feedback.title": "O que você achou das *opções*?",
  "briefing.feedback.subtitle":
    "Diga o que funcionou e o que não funcionou em cada uma. É o que faz a próxima busca vir mais certeira.",
  "briefing.feedback.cardRooms": "As opções que você viu",
  "briefing.feedback.help":
    "Marque o polegar em pelo menos uma opção. Se você já avaliou alguma na listagem, ela aparece aqui marcada.",
  "briefing.feedback.likedLabel": "O que você gostou",
  "briefing.feedback.dislikedLabel": "O que não serviu",
  "briefing.feedback.missedLabel": "O que faltou",
  "briefing.feedback.missedHelp":
    "Conte com as suas palavras o que impediu você de seguir com alguma delas.",
  "briefing.feedback.missedPlaceholder":
    "Ex.: a região é boa, mas todas ficaram longe do metrô.",
  "briefing.feedback.needLabel": "O que precisa ter",
  "briefing.feedback.needHelp":
    "O que a próxima opção não pode deixar de ter. Um item por linha.",
  "briefing.feedback.needPlaceholder":
    "Ex.: sala de reunião para 10 pessoas\nEx.: espaço para o time de vendas fazer ligação",
  "briefing.feedback.cardSearch": "A nova busca",
  "briefing.section.feedback": "Nova busca",
  "briefing.hint.feedback":
    "Avalie ao menos uma opção e preencha os campos marcados com *.",

  "briefing.intent.title": "O que você *procura*?",
  "briefing.intent.subtitle":
    "Sua resposta define o que vamos te mostrar e quem do nosso time assume o pedido.",
  "briefing.intent.label": "Escolha uma opção",
  "briefing.intent.option.readyOffices.title":
    "Outros escritórios prontos para ocupar",
  "briefing.intent.option.readyOffices.description":
    "Buscamos novas opções no inventário Woba, no mesmo formato das que você viu.",
  "briefing.intent.option.customizable.title":
    "Personalizar um dos escritórios que você viu",
  "briefing.intent.option.customizable.description":
    "Você escolhe uma das opções da proposta e diz o que mudaria: layout, mobiliário, marca ou a própria estrutura.",
  "briefing.intent.option.tailorMade.title":
    "Um espaço exclusivo, com obra sob medida",
  "briefing.intent.option.tailorMade.description":
    "Encontramos o imóvel e desenhamos o escritório do zero para o seu time. Prazo e investimento maiores.",
  // Aviso de preço sob Personalizável e Sob Medida (pedido do Growth).
  "briefing.priceNotice.customizable":
    "Um escritório personalizado custa cerca de 20% a 40% a mais que um escritório pronto.",
  "briefing.priceNotice.tailorMade":
    "Um escritório sob medida custa cerca de 20% a 40% a mais que um escritório pronto.",
  "briefing.priceNotice.accept": "Estou ciente e quero seguir",

  // Fluxo B · etapa 2
  // Três telas atrás de uma etapa: a pergunta 00 define o que se pede.
  "briefing.space.tailorMade.title": "Como é o *espaço* que você imagina?",
  "briefing.space.tailorMade.subtitle":
    "Já temos a região e o orçamento da sua proposta. Agora conte como o imóvel precisa ser — é a partir daqui que o projeto é desenhado.",
  "briefing.space.cardBuilding": "O imóvel",

  // Campos compartilhados
  "briefing.field.region.label": "Cidade e região / bairro desejado",
  "briefing.field.region.placeholder":
    "Ex.: São Paulo — Pinheiros ou Faria Lima",
  "briefing.field.region.help":
    "Começamos pela região da sua proposta. Mude para onde você quer procurar agora.",
  "briefing.field.people.label": "Número de pessoas",
  "briefing.field.people.placeholder": "Ex.: 24",
  "briefing.field.people.help":
    "Veio da sua proposta. Mude se o número for outro.",
  "briefing.field.deadline.label": "Prazo para entrar",
  "briefing.field.budget.label": "Orçamento mensal",
  "briefing.field.budget.placeholder": "Ex.: até R$ 30.000 por mês",
  "briefing.field.budget.help":
    "Veio da sua proposta. Opcional, mas ajuda a gente a só trazer o que cabe.",
  "briefing.field.mustHave.label": "O que não pode faltar",
  "briefing.field.mustHave.placeholder":
    "Ex.: sala de reunião para 10 pessoas\nEx.: espaço para o time de vendas fazer ligação",
  "briefing.field.mustHave.help": "Um item por linha.",
  "briefing.field.sizeM2.label": "Metragem aproximada (m²)",
  "briefing.field.sizeM2.placeholder": "Ex.: 300",
  "briefing.field.buildingType.label": "Tipo de imóvel",
  "briefing.field.standard.label": "Padrão desejado",
  "briefing.field.nearby.label": "Comodidades básicas obrigatórias",
  "briefing.field.priorities.label": "Suas três maiores prioridades",
  "briefing.field.priorities.help":
    "Em ordem: o que você olha primeiro quando compara dois espaços.",
  "briefing.field.priority1.placeholder": "1ª prioridade",
  "briefing.field.priority2.placeholder": "2ª prioridade",
  "briefing.field.priority3.placeholder": "3ª prioridade",
  "briefing.field.adjustments.label": "Mudanças que você faria no espaço",
  "briefing.field.likedSpace.label": "Sala que o cliente gostou",
  "briefing.field.missedNotes.label": "Detalhes do que faltou",
  "briefing.field.adjustmentNotes.label": "Detalhes das mudanças",
  "briefing.field.intent.label": "O que procura",
  "briefing.field.name.label": "Nome",
  "briefing.field.jobTitle.label": "Cargo",
  "briefing.field.email.label": "E-mail",
  "briefing.field.whatsapp.label": "WhatsApp",

  // Opções
  "briefing.option.adjustments.furnitureLayout": "Mudar o layout dos móveis",
  "briefing.option.adjustments.furnitureSwap":
    "Trocar ou acrescentar mobiliário",
  "briefing.option.adjustments.deskCount":
    "Mais ou menos posições na mesma sala",
  "briefing.option.adjustments.maintenance":
    "Consertar ou fazer manutenção em algo",
  "briefing.option.adjustments.storage": "Armários / espaço de guarda",
  "briefing.option.adjustments.branding": "Personalizar com a marca da empresa",
  "briefing.option.adjustments.otherRoomSameBuilding":
    "Ver outra sala no mesmo prédio",
  "briefing.option.adjustments.walls": "Derrubar ou erguer parede",
  "briefing.option.adjustments.internalMeetingRoom":
    "Criar sala de reunião ou cabine no espaço",
  "briefing.option.adjustments.flooringCeiling": "Trocar piso ou forro",
  "briefing.option.adjustments.installations":
    "Mexer em ar-condicionado, elétrica ou hidráulica",
  "briefing.option.adjustments.mergeRooms": "Unir duas salas",
  "briefing.option.intent.readyOffices":
    "Outros escritórios prontos para ocupar",
  "briefing.option.intent.customizable":
    "Personalizar um dos escritórios que viu",
  "briefing.option.intent.tailorMade":
    "Um espaço exclusivo, com obra sob medida",
  "briefing.option.deadline.upTo30Days": "Até 30 dias",
  "briefing.option.deadline.upTo3Months": "Em 1 a 3 meses",
  "briefing.option.deadline.upTo6Months": "Em 3 a 6 meses",
  "briefing.option.deadline.over6Months": "Mais de 6 meses",
  "briefing.option.deadline.noDate": "Ainda não tenho data",
  "briefing.option.buildingType.corporateSlab": "Laje em prédio corporativo",
  "briefing.option.buildingType.streetBuilding": "Casa ou imóvel de rua",
  "briefing.option.buildingType.either": "Tanto faz",
  "briefing.option.standard.premium": "Alto padrão (AAA)",
  "briefing.option.standard.intermediate": "Intermediário",
  "briefing.option.standard.simple": "Mais simples",
  "briefing.option.nearby.kitchen": "Copa ou refeitório",
  "briefing.option.nearby.buildingReception": "Recepção no prédio",
  "briefing.option.nearby.publicTransport": "Transporte público por perto",
  "briefing.option.nearby.restaurants": "Restaurantes por perto",
  "briefing.option.nearby.parking": "Estacionamento",

  // Seus dados
  // Obra sob medida · etapa 3 — o briefing completo do fluxo autônomo que
  // este caminho restaura (salas de reunião, prioridades, referências).
  "briefing.stepper.routine": "Time e prioridades",
  "briefing.routine.title": "Como o seu time vai *usar* o espaço?",
  "briefing.routine.subtitle":
    "O dia a dia, o que não pode faltar e referências que ajudam a gente a acertar mais rápido.",
  "briefing.routine.cardMeetingRooms": "Salas de reunião",
  "briefing.routine.cardPriorities": "O que é prioridade",
  "briefing.routine.cardReferences": "Referências",
  "briefing.space.cardSize": "Tamanho e prazo",
  "briefing.field.investment.label":
    "Para ter o espaço do seu jeito, você investiria:",
  "briefing.option.investment.sameValue": "O mesmo valor da proposta",
  "briefing.option.investment.aLittleMore": "Um pouco mais",
  "briefing.option.investment.whateverItTakes":
    "O que for preciso para o espaço ficar certo",
  "briefing.field.nearbyScope.label":
    "Podemos buscar também em regiões próximas?",
  "briefing.option.nearbyScope.sameRegion": "Só na mesma região",
  "briefing.option.nearbyScope.sameAndNearby":
    "Na mesma região e em regiões próximas",
  "briefing.field.parking.label": "Estacionamento",
  "briefing.field.parking.placeholder": "Quantas vagas você precisa? Ex.: 5",
  "briefing.field.meetingRooms.placeholder": "Quantas?",
  "briefing.field.meetingRoomsSmall.label": "Pequenas (até 4 pessoas)",
  "briefing.field.meetingRoomsMedium.label": "Médias (6 a 8 pessoas)",
  "briefing.field.meetingRoomsLarge.label": "Grandes (10 ou mais)",
  "briefing.field.niceToHave.label": "Seria bom ter",
  "briefing.field.niceToHave.placeholder":
    "Ex.: bicicletário, academia no prédio, terraço",
  "briefing.field.likedReference.label":
    "Algum escritório ou prédio que você viu e gostou? Conte o que pesou: endereço, prédio, planta, preço.",
  "briefing.field.likedReference.placeholder":
    "Ex.: o prédio X na Faria Lima, pela localização e pela planta",
  "briefing.field.discardedReference.label":
    "Algum que você descartou? Por quê?",
  "briefing.field.discardedReference.placeholder":
    "Ex.: o prédio Y, porque não tinha estacionamento",
  "briefing.section.routine": "Dia a dia, prioridades e referências",
  "briefing.hint.routine":
    "Marque ao menos uma comodidade e preencha a 1ª prioridade.",

  "briefing.contact.title": "Seus *dados*",
  "briefing.contact.subtitle": "Para quem devemos retornar com as opções.",

  // Dicas da barra inferior quando o Continuar está travado
  "briefing.hint.adjust": "Escolha a sala e pelo menos uma mudança.",
  "briefing.hint.intent": "Escolha uma das opções.",
  "briefing.hint.priceNotice": "Confirme o aviso de preço para seguir.",
  "briefing.hint.space":
    "Informe o prazo e o padrão — ou, se for ajustar, a sala e as mudanças.",
  "briefing.hint.contact": "Preencha seus dados para enviar.",

  // Títulos das seções do resumo enviado
  "briefing.section.adjust": "Ajustes na sala escolhida",
  "briefing.section.intent": "O que você procura",
  "briefing.section.space": "O espaço desejado",
  "briefing.section.contact": "Seus dados",

  // Tela final
  "briefing.sent.overline": "Pedido enviado",
  "briefing.sent.headline": "Respostas *enviadas*!",
  "briefing.sent.subtitleReadySearch":
    "A Bruna já vai procurar novas opções no nosso inventário, com as suas avaliações em mãos, e te apresentar o que encontrar.",
  "briefing.sent.subtitleReady":
    "Nosso time vai procurar novas opções com base no que você respondeu e volta com as alternativas.",
  "briefing.sent.subtitleCustomizable":
    "Seu pedido tem mudanças no espaço, então foi para o time de escritórios personalizados. Eles retornam com o que é possível fazer, o prazo e o investimento.",
  "briefing.sent.subtitleTailorMade":
    "Nosso time vai usar suas respostas para buscar o imóvel e desenhar o escritório do seu time. Você recebe uma proposta sob medida.",
  "briefing.sent.summaryLabel": "Resumo do que você enviou",
  "briefing.sent.postStep0Label": "Respostas enviadas",
  "briefing.sent.postStep0Time": "Concluído",
  "briefing.sent.postStep1LabelReady": "Busca de novas opções",
  "briefing.sent.postStep1TimeReady": "Em até 2 dias úteis",
  "briefing.sent.postStep1LabelBuild": "Estudo do seu pedido",
  "briefing.sent.postStep1TimeBuild": "Em até 7 dias",
  "briefing.sent.postStep2LabelReady": "Novas opções na proposta",
  "briefing.sent.postStep2TimeReady": "Após a busca",
  "briefing.sent.postStep2LabelBuild": "Proposta com prazo e investimento",
  "briefing.sent.postStep2TimeBuild": "Após o estudo",
  "briefing.sent.postStep3Label": "Assine e ocupe",
  "briefing.sent.postStep3TimeReady": "Até 15 dias",
  "briefing.sent.postStep3TimeBuild": "Prazo a combinar",

  // ── Rating (avaliação da recomendação) ──
  "rating.prompt": "Acertamos na escolha?",
  "rating.drawerPrompt": "Acertamos na escolha desse escritório?",
  "rating.thumbUp": "Gostei deste escritório",
  "rating.thumbDown": "Não gostei deste escritório",
  "rating.modalTitlePositive": "Boa! Acertamos na recomendação",
  "rating.modalTitleNegative": "Ainda não acertamos na recomendação",
  "rating.modalSupportPositive":
    "Sua avaliação ajuda a gente a entender o que fez sentido pra você.",
  "rating.modalSupportNegative":
    "Temos mais opções pra você. Sua avaliação ajuda a gente a trazer as mais certeiras.",
  "rating.questionNegative": "O que não encaixou?",
  "rating.questionPositive": "O que você mais gostou?",
  "rating.subReasonsLabel": "O que está faltando?",
  "rating.commentLabel": "Quer contar mais? (opcional)",
  "rating.commentPlaceholder": "Escreva aqui...",
  "rating.submit": "Enviar avaliação",
  "rating.confirmationTitle": "Recebido!",
  "rating.confirmationBody":
    "Vamos usar sua avaliação para melhorar as próximas sugestões.",
  "rating.close": "Fechar",
  "rating.reason.photos": "As fotos não ajudam a entender o escritório",
  "rating.reason.capacity": "Não comporta o número de pessoas que eu preciso",
  "rating.reason.location": "A região não funciona pra mim",
  "rating.reason.price": "Não vale o valor pedido",
  "rating.reason.layoutCondition":
    "O layout ou o estado do escritório não me agradou",
  "rating.reason.missingRequirements": "Falta algo que eu preciso",
  "rating.reason.capacityFit": "Comporta bem o meu time",
  "rating.reason.locationGreat": "A região é ótima pra mim",
  "rating.reason.visualAppeal": "Gostei do visual do escritório",
  "rating.reason.amenitiesComplete": "Tem tudo o que eu preciso",
  "rating.reason.priceValue": "Bom custo-benefício",
  "rating.subReason.amenities": "Amenidades",
  "rating.subReason.equipment": "Equipamentos",
  "rating.subReason.accessibility": "Acessibilidade",
  "rating.subReason.other": "Outras características",
};
